/**
 * Solicitudes list section for InspeccionDialog
 * Updated to use dynamic catalogos with cascading logic
 * Categoría → Item (filtered by categoria)
 * Categoría → Falla (filtered by categoria)
 * Includes provider selection for solicitudes with "procede" evaluation
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent } from '../ui/dialog';
import { FileText, Trash2, X, ZoomIn, Loader2, UserCheck, Mail, CheckCircle, Zap } from 'lucide-react';
import PhotoUpload, { getFileType, FileThumbnail, PreviewContent } from '../PhotoUpload';
import VoiceInput from '../VoiceInput';
import { proveedoresAPI, notificacionesContratistasAPI } from '../../utils/api';
import { toast } from 'sonner';

const SolicitudesList = ({ 
  solicitudes, 
  editingIndex,
  onUpdateSolicitud,
  onAdd,
  onDelete,
  onSetEditing,
  readOnly = false,
  isActaEnviada = false,
  catalogos = { categorias: [], lugares: [], items: [], fallas: [] },
  requerimientoId = null,
  onNotificacionEnviada = null,
  canSendOrPrint = false,
  programacion = null
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [proveedoresPorCategoria, setProveedoresPorCategoria] = useState({});
  const [loadingProveedores, setLoadingProveedores] = useState({});
  const [notificacionEstado, setNotificacionEstado] = useState({ notificado: false, auto: false });
  const [enviandoNotificacion, setEnviandoNotificacion] = useState(false);

  // Cargar estado de notificación
  useEffect(() => {
    if (requerimientoId) {
      notificacionesContratistasAPI.getEstado(requerimientoId)
        .then(res => setNotificacionEstado(res.data))
        .catch(() => {});
    }
  }, [requerimientoId]);

  // Cargar proveedores para cada categoría cuando evaluación es "procede"
  useEffect(() => {
    solicitudes.forEach(sol => {
      if (sol.evaluacion === 'procede' && sol.categoria && !proveedoresPorCategoria[sol.categoria]) {
        loadProveedoresParaCategoria(sol.categoria);
      }
    });
  }, [solicitudes]);

  const loadProveedoresParaCategoria = async (categoria) => {
    if (loadingProveedores[categoria] || proveedoresPorCategoria[categoria]) return;
    
    setLoadingProveedores(prev => ({ ...prev, [categoria]: true }));
    try {
      const response = await proveedoresAPI.getPorCategoria(categoria);
      // El backend ahora devuelve { proveedores: [], fallback: boolean }
      const data = response.data;
      const proveedoresList = data.proveedores || data || [];
      const isFallback = data.fallback || false;
      
      setProveedoresPorCategoria(prev => ({ 
        ...prev, 
        [categoria]: { 
          proveedores: proveedoresList, 
          fallback: isFallback 
        } 
      }));
    } catch (error) {
      console.error(`Error loading proveedores for ${categoria}:`, error);
      setProveedoresPorCategoria(prev => ({ 
        ...prev, 
        [categoria]: { proveedores: [], fallback: false } 
      }));
    } finally {
      setLoadingProveedores(prev => ({ ...prev, [categoria]: false }));
    }
  };

  // Verificar si hay proveedores asignados
  const hayProveedoresAsignados = useMemo(() => {
    return solicitudes.some(sol => sol.proveedor_id && sol.evaluacion === 'procede');
  }, [solicitudes]);

  // Manejar notificación a contratistas
  const handleNotificarContratistas = async () => {
    if (!requerimientoId || !hayProveedoresAsignados) {
      toast.warning('No hay proveedores asignados para notificar');
      return;
    }
    
    // Validar que la fecha y hora de inicio de trabajos estén guardadas
    if (!programacion?.fecha || !programacion?.horaInicio) {
      toast.error('Debe guardar la inspección antes de notificar. Asegúrese de establecer la Fecha y Hora de Inicio de Trabajos.');
      return;
    }
    
    setEnviandoNotificacion(true);
    try {
      const response = await notificacionesContratistasAPI.notificar(requerimientoId, false);
      const data = response.data;
      
      // Log para debugging
      console.log('Respuesta notificación:', data);
      
      if (data && data.success) {
        const mensaje = data.message || `Notificación enviada a ${data.enviados || 0} proveedor(es)`;
        toast.success(mensaje);
        setNotificacionEstado({ notificado: true, auto: false });
        if (onNotificacionEnviada) onNotificacionEnviada(data);
      } else if (data && data.enviados > 0) {
        // El backend puede devolver enviados > 0 aunque success sea undefined
        toast.success(`Notificación enviada a ${data.enviados} proveedor(es)`);
        setNotificacionEstado({ notificado: true, auto: false });
        if (onNotificacionEnviada) onNotificacionEnviada(data);
      } else {
        toast.error(data?.message || 'Error al enviar notificación');
      }
    } catch (error) {
      console.error('Error notificando contratistas:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Error al enviar notificación';
      toast.error(errorMessage);
    } finally {
      setEnviandoNotificacion(false);
    }
  };

  return (
    <>
    <Card className="border-[#E8E8E7]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText size={18} className="text-[#9d8272]" />
            Solicitudes ({solicitudes.length})
          </span>
          {/* Botón Notificar Contratistas */}
          {requerimientoId && !readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleNotificarContratistas}
              disabled={!hayProveedoresAsignados || enviandoNotificacion}
              className={`text-xs gap-1 ${
                notificacionEstado.notificado 
                  ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100' 
                  : 'border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272]/5'
              }`}
              data-testid="notificar-contratistas-btn"
              title={notificacionEstado.notificado ? 'Ya notificado - Clic para reenviar' : 'Enviar notificación a contratistas'}
            >
              {enviandoNotificacion ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              <span className="hidden sm:inline">Notificar contratistas</span>
              {notificacionEstado.notificado && (
                <>
                  <CheckCircle size={14} className="text-green-600" />
                  {notificacionEstado.auto && (
                    <Zap size={12} className="text-yellow-500" title="Enviado automáticamente" />
                  )}
                </>
              )}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {solicitudes.map((sol, index) => (
          <div 
            key={sol.id} 
            className={`border rounded-lg p-3 ${
              editingIndex === index && !readOnly ? 'border-[#9d8272] bg-white' : 'border-[#E8E8E7] bg-[#FAFAFA]'
            }`}
            data-testid={`solicitud-item-${index}`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-[#9d8272]">#{index + 1}</span>
                {sol.evaluacion && sol.evaluacion !== 'sin_evaluacion' && (
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] ${
                      sol.evaluacion === 'procede' ? 'bg-green-50 text-green-700 border-green-200' : 
                      'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {sol.evaluacion === 'procede' ? 'Procede' : 'No Procede'}
                  </Badge>
                )}
                {sol.isNew && (
                  <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                    Nueva
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                {!readOnly && !isActaEnviada && editingIndex !== index && !sol.isNew && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSetEditing(index)}
                    className="h-7 px-2 text-xs text-[#9d8272] hover:bg-[#9d8272]/10"
                    data-testid={`edit-solicitud-${index}`}
                  >
                    Editar
                  </Button>
                )}
                {!readOnly && !isActaEnviada && editingIndex === index && !sol.isNew && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSetEditing(null)}
                    className="h-7 px-2 text-xs text-gray-500"
                  >
                    Cerrar
                  </Button>
                )}
                {sol.isNew && !readOnly && !isActaEnviada && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(index)}
                    className="h-7 px-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-new-solicitud-${index}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
            
            {editingIndex === index || sol.isNew ? (
              <SolicitudEditForm 
                sol={sol} 
                index={index} 
                onUpdate={onUpdateSolicitud}
                catalogos={catalogos}
                proveedoresData={proveedoresPorCategoria[sol.categoria] || { proveedores: [], fallback: false }}
                loadingProveedores={loadingProveedores[sol.categoria] || false}
              />
            ) : (
              <SolicitudReadView 
                sol={sol} 
                onImageClick={(url) => setPreviewImage(url)}
                proveedoresData={proveedoresPorCategoria[sol.categoria] || { proveedores: [], fallback: false }}
              />
            )}
          </div>
        ))}
        
        {/* Add solicitud button */}
        {!readOnly && !isActaEnviada && (
          <Button
            variant="outline"
            size="sm"
            onClick={onAdd}
            className="w-full border-dashed border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272]/5"
            data-testid="add-solicitud-btn"
          >
            + Agregar Solicitud
          </Button>
        )}
      </CardContent>
    </Card>

    {/* File Preview Modal */}
    <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
      <DialogContent className="max-w-3xl p-2">
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPreviewImage(null)}
            className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full h-8 w-8 p-0 z-10"
          >
            <X size={16} />
          </Button>
          {previewImage && <PreviewContent url={previewImage} />}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Sub-component: Edit form for a solicitud with cascading dropdowns
const SolicitudEditForm = ({ sol, index, onUpdate, catalogos, proveedoresData = { proveedores: [], fallback: false }, loadingProveedores = false }) => {
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingFallas, setLoadingFallas] = useState(false);
  const [proveedorSearch, setProveedorSearch] = useState('');
  const [proveedorDropdownOpen, setProveedorDropdownOpen] = useState(false);
  
  const proveedores = proveedoresData.proveedores || [];
  const isFallback = proveedoresData.fallback || false;
  
  // Filtrar proveedores por búsqueda
  const filteredProveedores = useMemo(() => {
    if (!proveedorSearch.trim()) return proveedores;
    const searchLower = proveedorSearch.toLowerCase();
    return proveedores.filter(p => 
      p.nombre.toLowerCase().includes(searchLower) ||
      (p.empresa && p.empresa.toLowerCase().includes(searchLower))
    );
  }, [proveedores, proveedorSearch]);
  
  // Obtener nombre del proveedor seleccionado
  const proveedorSeleccionado = useMemo(() => {
    if (!sol.proveedor_id) return null;
    return proveedores.find(p => p.id === sol.proveedor_id);
  }, [sol.proveedor_id, proveedores]);

  // Filter active categorias
  const activeCategorias = useMemo(() => 
    (catalogos.categorias || []).filter(c => c.activo !== false), [catalogos.categorias]);
  
  // Filter active lugares
  const activeLugares = useMemo(() => 
    (catalogos.lugares || []).filter(l => l.activo !== false), [catalogos.lugares]);

  // Filter items based on selected categoria
  const filteredItems = useMemo(() => {
    if (!sol.categoria) return [];
    return (catalogos.items || []).filter(item => 
      item.activo !== false && 
      (item.categoria_nombre === sol.categoria || !item.categoria_nombre)
    );
  }, [sol.categoria, catalogos.items]);

  // Filter fallas based on selected categoria
  const filteredFallas = useMemo(() => {
    if (!sol.categoria) return [];
    return (catalogos.fallas || []).filter(falla => 
      falla.activo !== false && 
      (falla.categoria_nombre === sol.categoria || !falla.categoria_nombre)
    );
  }, [sol.categoria, catalogos.fallas]);

  // Handle categoria change - reset item and falla
  const handleCategoriaChange = (value) => {
    onUpdate(index, 'categoria', value);
    onUpdate(index, 'item', '');
    onUpdate(index, 'falla', '');
    
    setLoadingItems(true);
    setLoadingFallas(true);
    setTimeout(() => {
      setLoadingItems(false);
      setLoadingFallas(false);
    }, 300);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {/* Categoría */}
        <div>
          <Label className="text-[10px]">Categoría <span className="text-red-500">*</span></Label>
          {sol.isNew ? (
            <Select
              value={sol.categoria}
              onValueChange={handleCategoriaChange}
            >
              <SelectTrigger className={`h-8 text-xs ${sol.isNew && !sol.categoria ? 'border-red-300' : 'border-[#c5b8a2]'}`}>
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {activeCategorias.map((cat) => (
                  <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={sol.categoria || ''} readOnly className="h-8 text-xs border-[#c5b8a2] bg-gray-50" />
          )}
        </div>

        {/* Lugar */}
        <div>
          <Label className="text-[10px]">Lugar <span className="text-red-500">*</span></Label>
          {sol.isNew ? (
            <Select
              value={sol.lugar}
              onValueChange={(v) => onUpdate(index, 'lugar', v)}
            >
              <SelectTrigger className={`h-8 text-xs ${sol.isNew && !sol.lugar ? 'border-red-300' : 'border-[#c5b8a2]'}`}>
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {activeLugares.map((lugar) => (
                  <SelectItem key={lugar.id} value={lugar.nombre}>{lugar.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={sol.lugar || ''} readOnly className="h-8 text-xs border-[#c5b8a2] bg-gray-50" />
          )}
        </div>

        {/* Item - depends on categoria */}
        <div>
          <Label className="text-[10px]">Item <span className="text-red-500">*</span></Label>
          {(sol.isNew || (!sol.isNew && (!sol.item || sol.item === 'No especificado') && sol.categoria)) ? (
            loadingItems ? (
              <div className="h-8 flex items-center justify-center border rounded border-[#c5b8a2]">
                <Loader2 size={14} className="animate-spin text-[#9d8272]" />
              </div>
            ) : (
              <Select
                value={sol.item === 'No especificado' ? '' : (sol.item || '')}
                onValueChange={(v) => onUpdate(index, 'item', v)}
                disabled={!sol.categoria}
              >
                <SelectTrigger className={`h-8 text-xs ${!sol.item || sol.item === 'No especificado' ? 'border-red-300' : 'border-[#c5b8a2]'}`}>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {filteredItems.map((item) => (
                    <SelectItem key={item.id} value={item.nombre}>{item.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : (
            <Input value={sol.item || ''} readOnly className="h-8 text-xs border-[#c5b8a2] bg-gray-50" />
          )}
        </div>

        {/* Falla - depends on categoria */}
        <div>
          <Label className="text-[10px]">Falla <span className="text-red-500">*</span></Label>
          {(sol.isNew || (!sol.isNew && (!sol.falla || sol.falla === 'No especificado') && sol.categoria)) ? (
            loadingFallas ? (
              <div className="h-8 flex items-center justify-center border rounded border-[#c5b8a2]">
                <Loader2 size={14} className="animate-spin text-[#9d8272]" />
              </div>
            ) : (
              <Select
                value={sol.falla === 'No especificado' ? '' : (sol.falla || '')}
                onValueChange={(v) => onUpdate(index, 'falla', v)}
                disabled={!sol.categoria}
              >
                <SelectTrigger className={`h-8 text-xs ${!sol.falla || sol.falla === 'No especificado' ? 'border-red-300' : 'border-[#c5b8a2]'}`}>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {filteredFallas.map((falla) => (
                    <SelectItem key={falla.id} value={falla.nombre}>{falla.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          ) : (
            <Input value={sol.falla || ''} readOnly className="h-8 text-xs border-[#c5b8a2] bg-gray-50" />
          )}
        </div>
      </div>
      
      {/* Descripción */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-[10px]">Descripción <span className="text-red-500">*</span></Label>
          {sol.isNew && (
            <VoiceInput
              onTranscript={(text, isFinal) => {
                if (isFinal) {
                  const current = sol.descripcion || '';
                  onUpdate(index, 'descripcion', current ? `${current} ${text}` : text);
                }
              }}
            />
          )}
        </div>
        <Textarea
          value={sol.descripcion}
          onChange={(e) => onUpdate(index, 'descripcion', e.target.value)}
          readOnly={!sol.isNew}
          className={`text-xs ${sol.isNew && !sol.descripcion ? 'border-red-300' : 'border-[#c5b8a2]'} ${!sol.isNew ? 'bg-gray-50' : ''}`}
          rows={2}
          placeholder="Describa el problema o use el micrófono..."
        />
      </div>

      {/* Evaluación */}
      <div>
        <Label className="text-[10px]">Evaluación</Label>
        <Select
          value={sol.evaluacion}
          onValueChange={(v) => {
            onUpdate(index, 'evaluacion', v);
            // Limpiar proveedor si ya no procede
            if (v !== 'procede') {
              onUpdate(index, 'proveedor_id', null);
            }
          }}
        >
          <SelectTrigger className="h-8 text-xs border-[#c5b8a2]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sin_evaluacion">Sin evaluación</SelectItem>
            <SelectItem value="procede">Procede</SelectItem>
            <SelectItem value="no_procede">No Procede</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Proveedor - Solo visible cuando evaluación es "procede" */}
      {sol.evaluacion === 'procede' && (
        <div className="relative">
          <Label className="text-[10px] flex items-center gap-1">
            <UserCheck size={12} className="text-[#9d8272]" />
            Proveedor
            {isFallback && (
              <span className="text-[9px] text-amber-600 ml-1">(todos los proveedores)</span>
            )}
          </Label>
          
          {loadingProveedores ? (
            <div className="h-8 flex items-center justify-center border rounded border-[#c5b8a2]">
              <Loader2 size={12} className="animate-spin text-[#9d8272]" />
              <span className="ml-1 text-xs text-gray-500">Cargando...</span>
            </div>
          ) : (
            <div className="relative">
              {/* Input de búsqueda */}
              <Input
                type="text"
                value={proveedorDropdownOpen ? proveedorSearch : (proveedorSeleccionado ? `${proveedorSeleccionado.nombre} - (${proveedorSeleccionado.tipo_contacto_nombre}${proveedorSeleccionado.empresa ? ` - ${proveedorSeleccionado.empresa}` : ''})` : '')}
                onChange={(e) => {
                  setProveedorSearch(e.target.value);
                  if (!proveedorDropdownOpen) setProveedorDropdownOpen(true);
                }}
                onFocus={() => {
                  setProveedorDropdownOpen(true);
                  setProveedorSearch('');
                }}
                onBlur={() => {
                  // Delay para permitir click en opciones
                  setTimeout(() => setProveedorDropdownOpen(false), 200);
                }}
                placeholder="Buscar proveedor..."
                className="h-8 text-xs border-[#c5b8a2] pr-8"
                data-testid="proveedor-search-input"
              />
              {/* Botón limpiar */}
              {sol.proveedor_id && (
                <button
                  type="button"
                  onClick={() => {
                    onUpdate(index, 'proveedor_id', null);
                    setProveedorSearch('');
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
              
              {/* Dropdown de opciones */}
              {proveedorDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-[#c5b8a2] rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {/* Opción sin asignar */}
                  <div
                    className="px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 text-gray-500 italic border-b"
                    onMouseDown={() => {
                      onUpdate(index, 'proveedor_id', null);
                      setProveedorSearch('');
                      setProveedorDropdownOpen(false);
                    }}
                  >
                    Sin asignar
                  </div>
                  
                  {filteredProveedores.length > 0 ? (
                    filteredProveedores.map((prov) => (
                      <div
                        key={prov.id}
                        className={`px-3 py-2 text-xs cursor-pointer hover:bg-[#9d8272]/10 truncate ${
                          sol.proveedor_id === prov.id ? 'bg-[#9d8272]/20 font-medium' : ''
                        }`}
                        onMouseDown={() => {
                          onUpdate(index, 'proveedor_id', prov.id);
                          setProveedorSearch('');
                          setProveedorDropdownOpen(false);
                        }}
                        title={`${prov.nombre} - (${prov.tipo_contacto_nombre}${prov.empresa ? ` - ${prov.empresa}` : ''})`}
                      >
                        <span className="font-medium">{prov.nombre}</span>
                        <span className="text-gray-500"> - </span>
                        <span className="text-[#9d8272]">({prov.tipo_contacto_nombre}{prov.empresa ? ` - ${prov.empresa}` : ''})</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-xs text-gray-500 italic">
                      No se encontraron proveedores
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {proveedores.length === 0 && !loadingProveedores && (
            <p className="text-[10px] text-amber-600 mt-1">
              No hay proveedores disponibles
            </p>
          )}
        </div>
      )}

      {/* Archivos */}
      <div>
        <Label className="text-[10px] mb-2 block">Archivos (máx. 3)</Label>
        <PhotoUpload
          photos={sol.fotos || []}
          onChange={(fotos) => onUpdate(index, 'fotos', fotos)}
          maxPhotos={3}
        />
      </div>
    </div>
  );
};

// Sub-component: Read-only view of a solicitud
const SolicitudReadView = ({ sol, onImageClick, proveedoresData = { proveedores: [], fallback: false } }) => {
  const proveedores = proveedoresData.proveedores || [];
  
  // Encontrar el proveedor asignado
  const proveedorAsignado = sol.proveedor_id 
    ? proveedores.find(p => p.id === sol.proveedor_id) 
    : null;
  
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
      <div>
        <p className="text-[#888888]">Categoría</p>
        <p className="font-medium">{sol.categoria}</p>
      </div>
      <div>
        <p className="text-[#888888]">Lugar</p>
        <p className="font-medium">{sol.lugar}</p>
      </div>
      <div>
        <p className="text-[#888888]">Item</p>
        <p className="font-medium">{sol.item}</p>
      </div>
      <div>
        <p className="text-[#888888]">Falla</p>
        <p className="font-medium">{sol.falla || <span className="text-gray-400 italic">No especificado</span>}</p>
      </div>
      <div>
        <p className="text-[#888888]">Evaluación</p>
        <Badge variant="outline" className="text-[10px]">
          {sol.evaluacion?.replace(/_/g, ' ')}
        </Badge>
      </div>
      {/* Proveedor - Solo mostrar si evaluación es "procede" */}
      {sol.evaluacion === 'procede' && (
        <div className="col-span-2 sm:col-span-5">
          <p className="text-[#888888] flex items-center gap-1">
            <UserCheck size={10} />
            Proveedor
          </p>
          {proveedorAsignado ? (
            <p className="font-medium text-[#9d8272]">
              {proveedorAsignado.nombre} {proveedorAsignado.empresa ? `(${proveedorAsignado.empresa})` : ''}
            </p>
          ) : sol.proveedor_id ? (
            <p className="font-medium text-gray-500 italic">Proveedor asignado</p>
          ) : (
            <p className="text-gray-400 italic">Sin asignar</p>
          )}
        </div>
      )}
      <div className="col-span-2 sm:col-span-5">
        <p className="text-[#888888]">Descripción</p>
        <p className="font-medium whitespace-pre-wrap">{sol.descripcion}</p>
      </div>
      {sol.fotos?.length > 0 && (
        <div className="col-span-2 sm:col-span-5 flex gap-2 mt-1 flex-wrap">
          {sol.fotos.map((foto, i) => {
            const url = foto.url || foto;
            return (
              <div 
                key={i} 
                className="relative group cursor-pointer w-16 h-16 rounded border overflow-hidden"
                onClick={() => onImageClick(url)}
                data-testid={`photo-thumbnail-${i}`}
              >
                <FileThumbnail url={url} />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded">
                  <ZoomIn size={16} className="text-white" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SolicitudesList;
